import React, { useState, useEffect, useRef } from "react";
import { useSnackbar } from "components/GlobalSnackbar";
import { handleApiError } from "utils/errorHandler";
import {
  Grid,
  Card,
  Icon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import { buttonStyles } from "utils/buttonStyles";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "../examples/Tables/DataTable";
import CustomPagination from "components/CustomPagination";
import TambahZon from "components/STPopup/TambahZon";
import EditZon from "components/STPopup/EditZon";
import TambahKategoriKutipan from "components/STPopup/TambahKategoriKutipan";
import EditKategoriKutipan from "components/STPopup/EditKategoriKutipan";
import ZonSearch from "components/STSearch/ZonSearch";
import KategoriKutipanSearch from "components/STSearch/KategoriKutipanSearch";
import { useMaterialUIController } from "context";
import { apiCreateZone, apiUpdateZone, apiDeleteZone } from "api/zones";
import { apiCreateCategory, apiUpdateCategory, apiDeleteCategory } from "api/categories";
import { autoCapitalizePayload } from "utils/autoCapitalize";

// Data
import zoneData from "./data/zones";
import categoryData from "./data/categories";

export default function ZonDanKategoriKutipan() {
  const { showSuccess, showError } = useSnackbar();
  // Common states
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Zone states
  const [zonSearch, setZonSearch] = useState({
    zon: "",
  });
  const [openTambahZon, setOpenTambahZon] = useState(false);
  const [openEditZon, setOpenEditZon] = useState(false);
  const [openDeleteZonConfirm, setOpenDeleteZonConfirm] = useState(false);
  const [selectedZonRow, setSelectedZonRow] = useState(null);

  // Inline editing state for zones
  const [editingZonCell, setEditingZonCell] = useState(null);
  const [editZonValue, setEditZonValue] = useState('');
  const [savingZon, setSavingZon] = useState(false);

  // Zone pagination state
  const [currentZonPage, setCurrentZonPage] = useState(1);
  const [itemsPerZonPage] = useState(10);

  // Category states
  const [kategoriSearch, setKategoriSearch] = useState({
    kategori_kutipan: "",
    kod_kategori: "",
  });
  const [openTambahKategori, setOpenTambahKategori] = useState(false);
  const [openEditKategoriKutipan, setOpenEditKategoriKutipan] = useState(false);
  const [openDeleteKategoriConfirm, setOpenDeleteKategoriConfirm] = useState(false);
  const [selectedKategoriRow, setSelectedKategoriRow] = useState(null);

  // Inline editing state for categories
  const [editingKategoriCell, setEditingKategoriCell] = useState(null);
  const [editKategoriValue, setEditKategoriValue] = useState('');
  const [savingKategori, setSavingKategori] = useState(false);

  // Category pagination state
  const [currentKategoriPage, setCurrentKategoriPage] = useState(1);
  const [itemsPerKategoriPage] = useState(10);

  // Debounce refs and first-load guards for searches
  const zonDebounceRef = useRef();
  const kategoriDebounceRef = useRef();
  const isFirstZonLoadRef = useRef(true);
  const isFirstKategoriLoadRef = useRef(true);

  const [controller] = useMaterialUIController();
  const { darkMode, whiteSidenav, transparentSidenav } = controller;
  const shouldApplyStyle =
    (!darkMode && !whiteSidenav && !transparentSidenav) ||
    (darkMode && !transparentSidenav && whiteSidenav);

 // Zone row selection handler (single-select, can uncheck)
function handleZonRowSelect(row) {
  if (!row) {
    setSelectedZonRow(null);
    return;
  }
  setSelectedZonRow((prev) => (prev && prev.id === row.id ? null : row));
}

// Category row selection handler (single-select, can uncheck)
function handleKategoriRowSelect(row) {
  if (!row) {
    setSelectedKategoriRow(null);
    return;
  }
  setSelectedKategoriRow((prev) => (prev && prev.id === row.id ? null : row));
}

  // Inline editing handlers for Zones (defined before data hooks to avoid hoisting issues)
  const handleZonCellEdit = (rowId, column, currentValue, row) => {
    setEditingZonCell({ rowId, column, value: currentValue, row });
    setEditZonValue(currentValue);
  };

  const handleCancelZonEdit = () => {
    setEditingZonCell(null);
    setEditZonValue('');
  };

  const handleSaveZonEdit = async (allZones = []) => {
    if (!editingZonCell) return;

    if (!editZonValue || editZonValue.trim() === '') {
      showError('Nilai tidak boleh kosong');
      return;
    }

    // Check for duplicate zon (case-insensitive, excluding current row)
    const trimmedValue = editZonValue.trim().toLowerCase();
    const isDuplicate = allZones.some(
      zone => zone.id !== editingZonCell.row.id && zone.zon.toLowerCase() === trimmedValue
    );
    
    if (isDuplicate) {
      showError('Zon ini sudah wujud. Sila gunakan nama yang berbeza.');
      return;
    }

    setSavingZon(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const updateData = {
        zon: editZonValue.trim(),
        bahagian_pemantau: editingZonCell.row.bahagian_pemantau || user.bahagian || "",
      };

      await apiUpdateZone(editingZonCell.row.id, updateData);
      showSuccess('Zon berjaya dikemaskini');
      setEditingZonCell(null);
      setEditZonValue('');
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setSavingZon(false);
    }
  };

  const handleZonEditKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancelZonEdit();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveZonEdit();
    }
  };

  // Inline editing handlers for Categories
  const handleKategoriCellEdit = (rowId, column, currentValue, row) => {
    setEditingKategoriCell({ rowId, column, value: currentValue, row });
    setEditKategoriValue(currentValue);
  };

  const handleCancelKategoriEdit = () => {
    setEditingKategoriCell(null);
    setEditKategoriValue('');
  };

  const handleSaveKategoriEdit = async (allCategories = []) => {
    if (!editingKategoriCell) return;

    if (!editKategoriValue || editKategoriValue.trim() === '') {
      showError('Nilai tidak boleh kosong');
      return;
    }

    // Check for duplicates (case-insensitive, excluding current row)
    const trimmedValue = editKategoriValue.trim().toLowerCase();
    
    if (editingKategoriCell.column === 'kategori_kutipan') {
      const isDuplicate = allCategories.some(
        cat => cat.id !== editingKategoriCell.row.id && 
               cat.kategori_kutipan.toLowerCase() === trimmedValue
      );
      if (isDuplicate) {
        showError('Kategori Kutipan ini sudah wujud. Sila gunakan nama yang berbeza.');
        return;
      }
    } else if (editingKategoriCell.column === 'kod_kategori') {
      const isDuplicate = allCategories.some(
        cat => cat.id !== editingKategoriCell.row.id && 
               cat.kod_kategori.toLowerCase() === trimmedValue
      );
      if (isDuplicate) {
        showError('Kod Kategori ini sudah wujud. Sila gunakan kod yang berbeza.');
        return;
      }
    }

    setSavingKategori(true);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      
      // Build update data with all required fields
      const updateData = {
        kategori_kutipan: editingKategoriCell.column === 'kategori_kutipan' 
          ? editKategoriValue.trim() 
          : editingKategoriCell.row.kategori_kutipan,
        kod_kategori: editingKategoriCell.column === 'kod_kategori'
          ? editKategoriValue.trim()
          : editingKategoriCell.row.kod_kategori,
        bahagian_pemantau: editingKategoriCell.row.bahagian_pemantau || user.bahagian || "",
      };

      await apiUpdateCategory(editingKategoriCell.row.id, updateData);
      showSuccess('Kategori berjaya dikemaskini');
      setEditingKategoriCell(null);
      setEditKategoriValue('');
      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setSavingKategori(false);
    }
  };

  const handleKategoriEditKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancelKategoriEdit();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveKategoriEdit();
    }
  };

  // Get zone table data using the data hook
  const {
    columns: zonColumns,
    rows: zonRows,
    pagination: zonPagination,
    rawData: zonRawData = [],
  } = zoneData({
    onRowSelect: handleZonRowSelect,
    selectedRow: selectedZonRow,
    currentPage: currentZonPage,
    itemsPerPage: itemsPerZonPage,
    search: zonSearch,
    refreshTrigger,
    editingCell: editingZonCell,
    editValue: editZonValue,
    onCellEdit: handleZonCellEdit,
    onSaveEdit: () => handleSaveZonEdit(zonRawData),
    onCancelEdit: handleCancelZonEdit,
    onEditValueChange: setEditZonValue,
    onEditKeyDown: handleZonEditKeyDown,
    saving: savingZon,
  });

  // Get category table data using the data hook
  const {
    columns: kategoriColumns,
    rows: kategoriRows,
    pagination: kategoriPagination,
    rawData: kategoriRawData = [],
  } = categoryData({
    onRowSelect: handleKategoriRowSelect,
    selectedRow: selectedKategoriRow,
    currentPage: currentKategoriPage,
    itemsPerPage: itemsPerKategoriPage,
    search: kategoriSearch,
    refreshTrigger,
    editingCell: editingKategoriCell,
    editValue: editKategoriValue,
    onCellEdit: handleKategoriCellEdit,
    onSaveEdit: () => handleSaveKategoriEdit(kategoriRawData),
    onCancelEdit: handleCancelKategoriEdit,
    onEditValueChange: setEditKategoriValue,
    onEditKeyDown: handleKategoriEditKeyDown,
    saving: savingKategori,
  });

  // Zone search handlers
  const handleZonSearchChange = async (e) => {
    const newSearch = { ...zonSearch, [e.target.name]: e.target.value };
    setZonSearch(newSearch);
    // Reset to first page; actual refresh is debounced in effect
    setCurrentZonPage(1);
  };

  // Category search handlers
  const handleKategoriSearchChange = async (e) => {
    const newSearch = { ...kategoriSearch, [e.target.name]: e.target.value };
    setKategoriSearch(newSearch);
    // Reset to first page; actual refresh is debounced in effect
    setCurrentKategoriPage(1);
  };

  // Debounced refresh for Zon search
  useEffect(() => {
    const term = (zonSearch?.zon || "").trim();
    if (zonDebounceRef.current) clearTimeout(zonDebounceRef.current);
    if (isFirstZonLoadRef.current) {
      isFirstZonLoadRef.current = false;
      if (!term) return; // skip initial empty search
    }
    zonDebounceRef.current = setTimeout(() => {
      setRefreshTrigger((prev) => prev + 1);
    }, 400);
    return () => {
      if (zonDebounceRef.current) clearTimeout(zonDebounceRef.current);
    };
  }, [zonSearch.zon]);

  // Debounced refresh for Kategori search
  useEffect(() => {
    const kategoriTerm = (kategoriSearch?.kategori_kutipan || "").trim();
    const kodTerm = (kategoriSearch?.kod_kategori || "").trim();
    if (kategoriDebounceRef.current) clearTimeout(kategoriDebounceRef.current);
    if (isFirstKategoriLoadRef.current) {
      isFirstKategoriLoadRef.current = false;
      if (!kategoriTerm && !kodTerm) return; // skip initial empty search
    }
    kategoriDebounceRef.current = setTimeout(() => {
      setRefreshTrigger((prev) => prev + 1);
    }, 400);
    return () => {
      if (kategoriDebounceRef.current) clearTimeout(kategoriDebounceRef.current);
    };
  }, [kategoriSearch.kategori_kutipan, kategoriSearch.kod_kategori]);

  // Zone CRUD handlers
  const handleTambahZonSave = async (data) => {
    try {
      setLoading(true);
      // Add department info from logged-in user
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      // Backend expects DB column names: zon, bahagian_pemantau
      const payload = autoCapitalizePayload({
        zon: data.zon,
        bahagian_pemantau: user.bahagian || data.bahagian_pemantau || "",
      });
      await apiCreateZone(payload);
      setRefreshTrigger((prev) => prev + 1); // Refresh the table
      setOpenTambahZon(false); // Close the modal
    } catch (error) {
      // Handle specific validation errors
      if (error.message.includes("already exists")) {
        alert(
          "Zon dengan nama ini sudah wujud dalam bahagian anda. Sila gunakan nama zon yang lain."
        );
      } else if (error.message.includes("required")) {
        alert("Nama zon diperlukan. Sila masukkan nama zon.");
      } else if (
        error.message.includes("unique constraint") ||
        error.message.includes("duplicate")
      ) {
        alert("Zon sudah wujud dalam sistem. Sila gunakan nama zon yang lain.");
      } else if (error.message.includes("validation")) {
        alert("Ralat validasi data. Sila periksa medan yang diperlukan dan cuba lagi.");
      } else if (error.message.includes("network") || error.message.includes("fetch")) {
        alert("Ralat rangkaian. Sila periksa sambungan internet anda dan cuba lagi.");
      } else {
        alert(error.message || "Gagal menambah zon. Sila cuba lagi.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditZonClick = () => {
    if (selectedZonRow) setOpenEditZon(true);
  };

  const handleEditZonSave = async (editZonData) => {
    try {
      setLoading(true);
      // Backend expects DB column names: zon, bahagian_pemantau
      const updatedData = autoCapitalizePayload({
        zon: editZonData.zon,
        bahagian_pemantau: editZonData.bahagian_pemantau,
      });
      await apiUpdateZone(selectedZonRow.id, updatedData);
      setRefreshTrigger((prev) => prev + 1);
      setOpenEditZon(false);
      setSelectedZonRow(null);
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setLoading(false);
    }
  };

  const handleZonDelete = async () => {
    if (!selectedZonRow) return;
    // Show confirmation dialog instead of window.confirm
    setOpenDeleteZonConfirm(true);
  };

  const handleConfirmZonDelete = async () => {
    if (!selectedZonRow) return;
    try {
      setLoading(true);
      await apiDeleteZone(selectedZonRow.id);
      setRefreshTrigger((prev) => prev + 1);
      setSelectedZonRow(null);
      setOpenDeleteZonConfirm(false);
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelZonDelete = () => {
    setOpenDeleteZonConfirm(false);
  };

  // Category CRUD handlers
  const handleTambahKategoriSave = async (data) => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      // Backend expects DB column names: kategori_kutipan, kod_kategori, bahagian_pemantau
      const payload = autoCapitalizePayload({
        kategori_kutipan: data.kategori_kutipan,
        kod_kategori: data.kod_kategori,
        bahagian_pemantau: user.bahagian || data.bahagian_pemantau || "",
      });
      await apiCreateCategory(payload);
      setRefreshTrigger((prev) => prev + 1);
      setOpenTambahKategori(false);
    } catch (error) {
      setSnackbar({ open: true, message: "Gagal menambah kategori.", severity: "error" });

      // Handle specific validation errors
      if (
        error.message.includes("already exists") ||
        error.response?.data?.error?.includes("already exists")
      ) {
        alert(
          "Kod kategori ini sudah wujud dalam bahagian anda. Sila gunakan kod kategori yang lain."
        );
      } else if (
        error.message.includes("required") ||
        error.response?.data?.error?.includes("required")
      ) {
        alert("Kod kategori diperlukan. Sila masukkan kod kategori.");
      } else if (
        error.message.includes("unique constraint") ||
        error.message.includes("duplicate")
      ) {
        alert("Kategori kutipan sudah wujud dalam sistem. Sila gunakan nama kategori yang lain.");
      } else if (error.message.includes("validation") || error.message.includes("required")) {
        alert("Ralat validasi data. Sila periksa medan yang diperlukan dan cuba lagi.");
      } else if (error.message.includes("network") || error.message.includes("fetch")) {
        alert("Ralat rangkaian. Sila periksa sambungan internet anda dan cuba lagi.");
      } else {
        alert("Gagal menambah kategori kutipan. Sila cuba lagi.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditKategoriClick = () => {
    if (selectedKategoriRow) setOpenEditKategoriKutipan(true);
  };

  const handleEditKategoriSave = async (editCategoryData) => {
    try {
      setLoading(true);
      // Backend expects DB column names: kategori_kutipan, kod_kategori, bahagian_pemantau
      const updatedData = autoCapitalizePayload({
        kategori_kutipan: editCategoryData.kategori_kutipan,
        kod_kategori: editCategoryData.kod_kategori,
        bahagian_pemantau: editCategoryData.bahagian_pemantau,
      });
      await apiUpdateCategory(selectedKategoriRow.id, updatedData);
      setRefreshTrigger((prev) => prev + 1);
      setOpenEditKategoriKutipan(false);
      setSelectedKategoriRow(null);
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setLoading(false);
    }
  };

  const handleKategoriDelete = async () => {
    if (!selectedKategoriRow) return;
    // Show confirmation dialog instead of window.confirm
    setOpenDeleteKategoriConfirm(true);
  };

  const handleConfirmKategoriDelete = async () => {
    if (!selectedKategoriRow) return;
    try {
      setLoading(true);
      await apiDeleteCategory(selectedKategoriRow.id);
      setRefreshTrigger((prev) => prev + 1);
      setSelectedKategoriRow(null);
      setOpenDeleteKategoriConfirm(false);
    } catch (error) {
      handleApiError(error, showError);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelKategoriDelete = () => {
    setOpenDeleteKategoriConfirm(false);
  };

  // Zone pagination handler
  const handleZonPageChange = (newPage) => {
    setCurrentZonPage(newPage);
    setRefreshTrigger((prev) => prev + 1);
  };

  // Category pagination handler
  const handleKategoriPageChange = (newPage) => {
    setCurrentKategoriPage(newPage);
    setRefreshTrigger((prev) => prev + 1);
  };

  const handleZonNextPage = () => {
    if (currentZonPage < zonPagination.totalPages) {
      setCurrentZonPage(prev => prev + 1);
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const handleZonPrevPage = () => {
    if (currentZonPage > 1) {
      setCurrentZonPage(prev => prev - 1);
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const handleKategoriNextPage = () => {
    if (currentKategoriPage < kategoriPagination.totalPages) {
      setCurrentKategoriPage(prev => prev + 1);
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  const handleKategoriPrevPage = () => {
    if (currentKategoriPage > 1) {
      setCurrentKategoriPage(prev => prev - 1);
      setRefreshTrigger((prev) => prev + 1);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={2}>
        <Grid container spacing={2}>
          {/* Zones Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.info}
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Senarai Zon
                </MDTypography>
              </MDBox>
              <MDBox>
                <ZonSearch
                  search={zonSearch}
                  handleSearchChange={handleZonSearchChange}
                  onTambah={() => setOpenTambahZon(true)}
                  onDelete={handleZonDelete}
                  selectedRow={selectedZonRow}
                  loading={loading}
                />
                <DataTable
                  table={{ columns: zonColumns, rows: zonRows }}
                  showTotalEntries={false}
                  isSorted={false}
                  noEndBorder
                  entriesPerPage={false}
                  canSearch={false}
                />
                {zonPagination.totalItems > 10 && (
                  <CustomPagination
                    currentPage={zonPagination.currentPage}
                    totalPages={zonPagination.totalPages}
                    totalItems={zonPagination.totalItems}
                    itemsPerPage={itemsPerZonPage}
                    actualItemsOnPage={zonPagination.actualItemsOnPage}
                    onPageChange={handleZonPageChange}
                    onNextPage={handleZonNextPage}
                    onPrevPage={handleZonPrevPage}
                    variant="contained"
                    color="info"
                  />
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Categories Section */}
          <Grid item xs={12} md={6}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                sx={pageHeaderStyles.info}
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  Senarai Kategori Kutipan
                </MDTypography>
              </MDBox>
              <MDBox>
                <KategoriKutipanSearch
                  search={kategoriSearch}
                  handleSearchChange={handleKategoriSearchChange}
                  onTambah={() => setOpenTambahKategori(true)}
                  onDelete={handleKategoriDelete}
                  selectedRow={selectedKategoriRow}
                  loading={loading}
                />
                <DataTable
                  table={{ columns: kategoriColumns, rows: kategoriRows }}
                  showTotalEntries={false}
                  isSorted={false}
                  noEndBorder
                  entriesPerPage={false}
                  canSearch={false}
                />
                {kategoriPagination.totalItems > 10 && (
                  <CustomPagination
                    currentPage={kategoriPagination.currentPage}
                    totalPages={kategoriPagination.totalPages}
                    totalItems={kategoriPagination.totalItems}
                    itemsPerPage={itemsPerKategoriPage}
                    actualItemsOnPage={kategoriPagination.actualItemsOnPage}
                    onPageChange={handleKategoriPageChange}
                    onNextPage={handleKategoriNextPage}
                    onPrevPage={handleKategoriPrevPage}
                    variant="contained"
                    color="info"
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Zone Modals */}
      <TambahZon
        open={openTambahZon}
        onClose={() => setOpenTambahZon(false)}
        onSave={handleTambahZonSave}
      />
      <EditZon
        open={openEditZon}
        data={selectedZonRow}
        onClose={() => setOpenEditZon(false)}
        onSave={handleEditZonSave}
      />

      {/* Category Modals */}
      <TambahKategoriKutipan
        open={openTambahKategori}
        onClose={() => setOpenTambahKategori(false)}
        onSave={handleTambahKategoriSave}
      />
      <EditKategoriKutipan
        open={openEditKategoriKutipan}
        data={selectedKategoriRow}
        onClose={() => setOpenEditKategoriKutipan(false)}
        onSave={handleEditKategoriSave}
      />

      {/* Delete Confirmation Dialogs */}
      {/* Zone Delete Confirmation */}
      <Dialog open={openDeleteZonConfirm} onClose={handleCancelZonDelete} maxWidth="xs">
        <DialogTitle
          sx={{
            backgroundColor: "#ff0000ff",
            borderBottom: "1px solid #e0e0e0",
            fontSize: "1.1rem",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Pengesahan Padam Zon
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "0.95rem",
              lineHeight: 1.6,
              color: "#333",
              mb: selectedZonRow?.zon ? 2 : 0,
              mt: 2,
            }}
          >
            Adakah anda pasti untuk memadam zon ini?
          </Typography>
          {selectedZonRow?.zon && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                color: "#ff0000",
                fontWeight: "medium",
              }}
            >
              Zon: {selectedZonRow.zon}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmZonDelete}
            variant="contained"
            size="small"
            sx={{
              ...buttonStyles.delete,
              m: 0,
              px: 2,
              minWidth: 0
            }}
          >
            HAPUS
          </Button>
          <Button
            onClick={handleCancelZonDelete}
            variant="contained"
            size="small"
            sx={{
              ...buttonStyles.cancel,
              m: 0,
              px: 2,
              minWidth: 0
            }}
          >
            BATAL
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Delete Confirmation */}
      <Dialog open={openDeleteKategoriConfirm} onClose={handleCancelKategoriDelete} maxWidth="xs">
        <DialogTitle
          sx={{
            backgroundColor: "#ff0000ff",
            borderBottom: "1px solid #e0e0e0",
            fontSize: "1.1rem",
            fontWeight: "bold",
            color: "white !important",
          }}
        >
          Pengesahan Padam Kategori
        </DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: "0.95rem",
              lineHeight: 1.6,
              color: "#333",
              mb: selectedKategoriRow?.kategori_kutipan ? 2 : 0,
              mt: 2,
            }}
          >
            Adakah anda pasti untuk memadam kategori kutipan ini?
          </Typography>
          {selectedKategoriRow?.kategori_kutipan && (
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.85rem",
                color: "#ff0000",
                fontWeight: "medium",
              }}
            >
              Kategori: {selectedKategoriRow.kategori_kutipan}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleConfirmKategoriDelete}
            variant="contained"
            size="small"
            sx={{
              ...buttonStyles.delete,
              m: 0,
              px: 2,
              minWidth: 0
            }}
          >
            HAPUS
          </Button>
          <Button
            onClick={handleCancelKategoriDelete}
            variant="contained"
            size="small"
            sx={{
              ...buttonStyles.cancel,
              m: 0,
              px: 2,
              minWidth: 0
            }}
          >
            BATAL
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
